import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { AlertOctagon, AlertTriangle, Info, CheckCircle2 } from 'lucide-react';

const SEVERITY_STYLE = {
  HARD_BLOCK: { border: 'border-red-300', bg: 'bg-red-50', text: 'text-red-800', icon: AlertOctagon, iconColor: 'text-red-600', label: 'Hard Block' },
  BLOCKING: { border: 'border-amber-300', bg: 'bg-amber-50', text: 'text-amber-900', icon: AlertTriangle, iconColor: 'text-amber-600', label: 'Needs Justification' },
  INFO: { border: 'border-sky-200', bg: 'bg-sky-50', text: 'text-sky-900', icon: Info, iconColor: 'text-sky-600', label: 'Info' },
};

export function FlagCard({ flag, onJustify, disabled }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const style = SEVERITY_STYLE[flag.severity] || SEVERITY_STYLE.INFO;
  const Icon = style.icon;

  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await onJustify(flag.id, text.trim());
      setText('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-sm border ${style.border} ${style.bg} p-3`} data-testid={`flag-${flag.code}`}>
      <div className="flex items-start gap-2">
        <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${style.iconColor}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-semibold uppercase tracking-wide ${style.text}`}>{flag.code.replace(/_/g, ' ')}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-black/5 text-muted-foreground">{style.label}</span>
          </div>
          <p className={`text-sm mt-0.5 ${style.text}`}>{flag.message}</p>
          {flag.details?.pi_text && flag.details?.po_item_name && (
            <div className="mt-1.5 grid grid-cols-2 gap-2 text-xs" data-testid={`flag-compare-${flag.code}`}>
              <div className="bg-white/70 border border-border rounded-sm p-1.5">
                <span className="text-[10px] uppercase text-muted-foreground block">PI Description</span>
                {flag.details.pi_text}
              </div>
              <div className="bg-white/70 border border-border rounded-sm p-1.5">
                <span className="text-[10px] uppercase text-muted-foreground block">PO Item Name</span>
                {flag.details.po_item_name}
              </div>
            </div>
          )}

          {flag.justification ? (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5" data-testid={`flag-justified-${flag.code}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span><strong>{flag.justified_by_name || 'Uploader'}:</strong> {flag.justification}</span>
            </div>
          ) : flag.severity === 'BLOCKING' && !disabled ? (
            <div className="mt-2 flex items-center gap-2">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Explain why this is OK to proceed..."
                rows={1}
                className="text-xs bg-white"
                data-testid={`flag-justify-input-${flag.code}`}
              />
              <Button size="sm" className="rounded-sm shrink-0" disabled={!text.trim() || saving} onClick={handleSave} data-testid={`flag-justify-btn-${flag.code}`}>
                {saving ? '...' : 'Justify'}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
